import Model from "../../Model.js";
import MonthPeriod from "../MonthPeriod.js";
import RuleModelFactory from "../ruleModels/RuleModelFactory.js";

/**
 * Template of rule, persisted on database.
 * This template is later interpreted in order to produce instances of `Rule`.
 *
 * @param {string} id - Unique identifier
 * @param {string} rowId - Identifier of the row on which the rule will be applied. We can not materialize
 *   the row here as the row might be "volatile". During the report interpretation, it may happen that
 *   some row templates are generated, then interpreted. Thus, there is no guarantee that the row id actually
 *   exists in the template outside the interpretation process.
 * @param {MonthPeriod} start - The forecast period to which the
 *   rule template is associated
 * @param {AbstractRuleModel} ruleModel - Configuration of the rule
 * template. Can be for example an instance of `BalanceRuleModel` or of
 * `ConstantPredictionRuleModel`
 * @param {string} [forecastTemplateId] - Identifier of the forecast
 *   template containing the report template that this model refers to
 */
export default class RuleTemplate extends Model {
	constructor({ id, rowId, start, ruleModel, forecastTemplateId } = {}) {
		super(...arguments);

		this._id = id;
		this._rowId = rowId;
		this._start = start;
		this._ruleModel = ruleModel;
		this._forecastTemplateId = forecastTemplateId;
	}

	getId() {
		return this._id;
	}

	getRowId() {
		return this._rowId;
	}

	getStart() {
		return this._start;
	}

	getRuleModel() {
		return this._ruleModel;
	}

	setRuleModel(ruleModel) {
		this._ruleModel = ruleModel;
	}

	getRuleModelKind() {
		return this._ruleModel.getKind();
	}

	getRuleModelLabel() {
		return this._ruleModel.constructor.getLabel();
	}

	getDescription() {
		return this._ruleModel.getDescription();
	}

	getRuleModelDescriptionText() {
		return this._ruleModel.constructor.getDescriptionText();
	}

	setForecastTemplateId(forecastTemplateId) {
		this._forecastTemplateId = forecastTemplateId;
	}

	/**
	 * Return true if the receiver is assigned to the given cell and
	 * if its rule model inherits from `ruleModelClass`.
	 *
	 * @param {string} rowId - The vertical position of the cell.
	 * @param {MonthPeriod} start - The horizontal position of the cell.
	 * @param {class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 *   filter by this rule model class
	 */
	match({ rowId, start, ruleModelClass }) {
		return (
			rowId === this._rowId &&
			this._start.equal(start) &&
			this._ruleModel instanceof ruleModelClass
		);
	}

	matchManualJournalEntry({ accountNumber, start }) {
		return this._ruleModel.matchesManualJournalEntry({
			ruleStartPeriod: this._start,
			monthPeriod: start,
			accountNumber,
		});
	}

	isPrediction() {
		return this._ruleModel.isPrediction();
	}

	isBalance() {
		return this._ruleModel.isBalance();
	}

	isManualEntry() {
		return this._ruleModel.isManualEntry();
	}

	isManualJournalEntry() {
		return this._ruleModel.isManualJournalEntry();
	}

	isBeta() {
		return this._ruleModel.isBeta();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.rowId = this._rowId;
		properties.start = this._start;
		properties.ruleModel = this._ruleModel;
		properties.forecastTemplateId = this._forecastTemplateId;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.ruleModel &&= RuleModelFactory.materialize(
			data.ruleModel,
			context,
		);

		data.start = MonthPeriod.materialize(data.start, context);
	}
}
